// src/pages/plantation/monitor-setara-urea-per-umur/MonitorSetaraUreaPerUmur.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    Stack,
    Snackbar,
    TextField,
    Tooltip,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from "@mui/icons-material/Refresh";

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, push, update } from "firebase/database";

// Import XLSX untuk handling file Excel
import * as XLSX from "xlsx";

// Import dari file utilitas bibit-extractor
import {
    getKelasBibit,
    getJenisBibit
} from "../../../utils/bibit-extractor";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimalWithTwoDigits,
    formatDecimal,
    formatText,
    formatValueAsIs,
    formatDateToDDMMYYYY
} from "../../../utils/formatters-master";

import {
  calculateAllValues
} from "../formula/Calculation";

// Import fungsi hitung umur
import { 
    calculateUmur
} from "../formula/hitung-umur";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

// Import FilterPanel baru
import FilterPanelMonitorSetaraUrea from "./FilterPanelMonitorSetaraUrea";

import AkumUreaBibitBesar from "../setara-urea/AkumUreaBibitBesar";
import AkumUreaBibitSedang from "../setara-urea/AkumUreaBibitSedang";
import AkumUreaBibitKecil from "../setara-urea/AkumUreaBibitKecil";
import AkumUreaRatoon from "../setara-urea/AkumUreaRatoon";

import GypsumPerMonth from "../gypsum/GypsumPerMonth";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find(lokasi => lokasi.id_lokasi === id_lokasi);
};

// Fungsi untuk import Excel ke Firebase untuk setara urea per umur
const importExcelToFirebaseSetaraUrea = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1, 
                    raw: true
                });
                
                //for (let i = 0; i < Math.min(10, jsonData.length); i++) {
                //    console.log(`Row ${i}:`, jsonData[i]);
                //}
                
                const dataStartRow = 4; // B6 (0-based index: 5)
                
                const columnMapping = {
                    id_lokasi: 0, 
                    rencana_forcing: 3,
                    real_forcing: 4,
                    gypsum: 5,
                    kcl: 6, 
                    k2so4: 7, 
                    za: 8, 
                    dap: 9, 
                    urea: 10 
                };

                if (jsonData.length > dataStartRow) {
                    const sampleRow = jsonData[dataStartRow];
                    //console.log("Row 6 raw:", sampleRow);
                    //console.log("id_lokasi:", sampleRow[columnMapping.id_lokasi]);
                    //console.log("rencana_forcing:", sampleRow[columnMapping.rencana_forcing]);
                    //console.log("real_forcing:", sampleRow[columnMapping.real_forcing]);
                    //console.log("gypsum:", sampleRow[columnMapping.gypsum]);
                    //console.log("kcl:", sampleRow[columnMapping.kcl]);
                }

                const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
                const existingSetaraUreaSnapshot = await onValuePromise(setaraUreaRef);
                const existingSetaraUreaData = existingSetaraUreaSnapshot.val() || {};

                const existingSetaraUreaMap = new Map();
                Object.keys(existingSetaraUreaData).forEach(fbKey => {
                    const record = existingSetaraUreaData[fbKey];
                    if (record.id_lokasi) {
                        const key = record.id_lokasi.toString().trim();
                        existingSetaraUreaMap.set(key, fbKey);
                    }
                });

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;

                // Proses data
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const id_lokasi = row[columnMapping.id_lokasi];
                    const rencana_forcing = row[columnMapping.rencana_forcing];
                    const real_forcing = row[columnMapping.real_forcing];
                    const gypsum = row[columnMapping.gypsum];
                    const kcl = row[columnMapping.kcl];
                    const k2so4 = row[columnMapping.k2so4];
                    const za = row[columnMapping.za];
                    const dap = row[columnMapping.dap];
                    const urea = row[columnMapping.urea];
                    
                    // Skip jika id_lokasi kosong
                    if (!id_lokasi || (typeof id_lokasi === 'string' && id_lokasi.trim() === '')) {
                        ignoredCount++;
                        continue;
                    }

                    const formattedIdLokasi = String(id_lokasi).trim();

                    // Data dari Excel yang akan disimpan di Firebase
                    const excelDataToUpdate = {
                        id_lokasi: formattedIdLokasi,
                        rencana_forcing: formatDateToDDMMYYYY(rencana_forcing),
                        real_forcing: formatDateToDDMMYYYY(real_forcing),
                        gypsum: formatValueAsIs(gypsum), 
                        kcl: formatValueAsIs(kcl),
                        k2so4: formatValueAsIs(k2so4),
                        za: formatValueAsIs(za),
                        dap: formatValueAsIs(dap),
                        urea: formatValueAsIs(urea),
                        last_imported_at: importTimestamp,
                    };

                    const firebaseKey = existingSetaraUreaMap.get(formattedIdLokasi);

                    try {
                        if (firebaseKey) {
                            // Update data yang sudah ada
                            await update(ref(database, `db_plantation/tb_setara_urea_per_umur/${firebaseKey}`), excelDataToUpdate);
                            updatedCount++;
                        } else {
                            // Buat data baru
                            await push(setaraUreaRef, excelDataToUpdate);
                            importedCount++;
                        }
                        
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        ignoredCount++;
                    }
                }

                //console.log(`Hasil import: ${importedCount} baru, ${updatedCount} update, ${ignoredCount} abaikan`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    ignoredCount,
                    total: importedCount + updatedCount
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

// Fungsi untuk refresh data setelah import
const refreshSetaraUreaData = async (database, onValuePromise) => {
    const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
    const updatedSetaraUreaSnapshot = await onValuePromise(setaraUreaRef);
    const updatedSetaraUreaData = updatedSetaraUreaSnapshot.val() || {};
    
    const updatedLoadedData = [];
    Object.keys(updatedSetaraUreaData).forEach(key => {
        const record = updatedSetaraUreaData[key];
        updatedLoadedData.push({ 
            firebaseKey: key, 
            id_lokasi: record.id_lokasi || '',
            rencana_forcing: record.rencana_forcing || '',
            real_forcing: record.real_forcing || '',
            gypsum: record.gypsum || '',
            kcl: record.kcl || '',
            k2so4: record.k2so4 || '',
            za: record.za || '',
            dap: record.dap || '',
            urea: record.urea || '',
        });
    });
    
    return updatedLoadedData;
};

// Fungsi utilitas untuk format tanggal MulaiRawat dan tanggal_closing
const displayFormatDateToDDMMYYYY = (dateString) => {
    if (!dateString || dateString === '' || dateString === '-') return '-';
    
    // Normalisasi string
    const normalized = dateString.toString().trim();
    
    // Jika sudah format DD-MM-YYYY, biarkan as is
    if (normalized.includes('-') && normalized.split('-').length === 3) {
        const parts = normalized.split('-');
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return normalized;
        }
    }
    
    // Jika format DD/MM/YYYY, ubah ke DD-MM-YYYY
    if (normalized.includes('/') && normalized.split('/').length === 3) {
        const parts = normalized.split('/');
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
    }
    
    // Jika format YYYY-MM-DD, ubah ke DD-MM-YYYY
    if (normalized.includes('-') && normalized.split('-').length === 3) {
        const parts = normalized.split('-');
        if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
    }
    
    // Coba parse sebagai Date object
    try {
        const date = new Date(normalized);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        }
    } catch (e) {
        // ignore
    }
    
    return normalized; // Return as is jika format tidak dikenali
};

// Helper: ekstrak tanggal closing dari hasilPanenData berdasarkan id_lokasi
const getTanggalClosingFromHasilPanen = (idLokasi, hasilPanenData) => {
    if (!idLokasi || !hasilPanenData || typeof hasilPanenData !== 'object') {
        return null;
    }
    
    // Cari data dengan id_lokasi yang sama
    for (const key in hasilPanenData) {
        const record = hasilPanenData[key];
        if (record && record.id_lokasi && 
            record.id_lokasi.toString() === idLokasi.toString() &&
            record.tanggal_closing && 
            record.tanggal_closing.trim() !== '' &&
            record.tanggal_closing !== '-') {
            
            return displayFormatDateToDDMMYYYY(record.tanggal_closing);
        }
    }
    
    return null;
};

function MonitorSetaraUreaPerUmur() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshLoading, setRefreshLoading] = useState(false);

    // State untuk data konfigurasi dropdown
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [lokasiData, setLokasiData] = useState([]); // Array data dari db_freshpine/tb_lokasi
    const [hasilPanenData, setHasilPanenData] = useState(null); // Data dari db_harvesting/tb_monitor_hasil_panen
    const [pjWilayahData, setPjWilayahData] = useState([]); // Data dari db_config/db_helper/tb_pj_wilayah
    const [plantCareList, setPlantCareList] = useState([]); // Data dari db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care
    
    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Shadow style yang konsisten (sama seperti di DashboardHeader)
    const paperShadow = "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)";

    // State untuk filter baru (mengadopsi gaya DashboardHeader)
    const [selectedFilters, setSelectedFilters] = useState({
        statusLokasi: null,
        kodeWilayah: null,
        wilayahPlantCare: null,
        jenisBibit: null,
        kelasBibit: null
    });

    // State untuk opsi filter (akan diisi berdasarkan data yang ada)
    const [filterOptions, setFilterOptions] = useState({
        statusLokasi: [],
        kodeWilayah: [],
        wilayahPlantCare: [],
        jenisBibit: [],
        kelasBibit: []
    });

    const [lokasiMulaiRawatMap, setLokasiMulaiRawatMap] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_plantation/tb_setara_urea_per_umur
                const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
                const setaraUreaSnapshot = await onValuePromise(setaraUreaRef);
                const setaraUreaData = setaraUreaSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (setaraUreaData) {
                    Object.keys(setaraUreaData).forEach(key => {
                        const record = setaraUreaData[key];
                        loadedData.push({ 
                            firebaseKey: key, 
                            id_lokasi: record.id_lokasi || '',
                            rencana_forcing: record.rencana_forcing || '',
                            real_forcing: record.real_forcing || '',
                            gypsum: record.gypsum || '',
                            kcl: record.kcl || '',
                            k2so4: record.k2so4 || '',
                            za: record.za || '',
                            dap: record.dap || '',
                            urea: record.urea || '',
                        });
                        
                        // Cari waktu update terakhir dari last_imported_at
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Fetch data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();
                
                let lokasiArray = [];
                let mulaiRawatMap = {};

                if (lokasiRawData) {
                    // Konversi dari object Firebase ke array
                    lokasiArray = Object.keys(lokasiRawData).map(key => ({
                        firebaseKey: key,
                        ...lokasiRawData[key]
                    }));
                    setLokasiData(lokasiArray);

                    lokasiArray.forEach(lokasi => {
                        if (lokasi.id_lokasi && lokasi.mulai_rawat) {
                            mulaiRawatMap[lokasi.id_lokasi] = displayFormatDateToDDMMYYYY(lokasi.mulai_rawat);
                        }
                    });
                    
                    setLokasiMulaiRawatMap(mulaiRawatMap);
                }

                // Fetch data from db_harvesting/tb_monitor_hasil_panen
                const hasilPanenRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
                const hasilPanenSnapshot = await onValuePromise(hasilPanenRef);
                const hasilPanenRawData = hasilPanenSnapshot.val();
                setHasilPanenData(hasilPanenRawData);

                // Fetch data from db_config/db_helper/tb_pj_wilayah
                const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
                const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
                const pjWilayahRawData = pjWilayahSnapshot.val();
                
                let pjWilayahArray = [];
                if (pjWilayahRawData) {
                    // Struktur: key adalah lokasi (misal: "401A"), value adalah data
                    pjWilayahArray = Object.keys(pjWilayahRawData).map(key => ({
                        lokasi: key, // Key adalah nama lokasi
                        ...pjWilayahRawData[key]
                    }));
                    //console.log("Data PJ Wilayah diambil:", pjWilayahArray.length, "items");
                    //console.log("Contoh data PJ Wilayah:", pjWilayahArray.slice(0, 3));
                }
                setPjWilayahData(pjWilayahArray);

                // Fetch data from db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care
                const plantCareRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care");
                const plantCareSnapshot = await onValuePromise(plantCareRef);
                const plantCareRawData = plantCareSnapshot.val();
                
                let plantCareArray = [];
                if (plantCareRawData) {
                    // Buat mapping dari ID ke nama
                    plantCareArray = Object.keys(plantCareRawData).map(key => {
                        const item = plantCareRawData[key];
                        return {
                            id: item.id_kepala_kebun_plant_care || key,
                            nama: item.nama_kepala_kebun_plant_care || ''
                        };
                    });
                    //console.log("Data Plant Care diambil:", plantCareArray.length, "items");
                    //console.log("Contoh data Plant Care:", plantCareArray.slice(0, 3));
                }
                setPlantCareList(plantCareArray);

                // Update dataList dengan informasi dari tb_lokasi dan tb_pj_wilayah
                const updatedDataList = loadedData.map((item) => {
                    const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);
                    
                    // Dapatkan tanggal mulai rawat
                    let mulaiRawat = '-';
                    if (lokasiInfo && lokasiInfo.mulai_rawat) {
                        mulaiRawat = displayFormatDateToDDMMYYYY(lokasiInfo.mulai_rawat);
                    }
                    
                    // Dapatkan tanggal closing dari hasil panen
                    const tanggalClosing = getTanggalClosingFromHasilPanen(item.id_lokasi, hasilPanenRawData);
                    
                    // Hitung umur menggunakan fungsi calculateUmur
                    let calculatedUmur = '-';
                    if (mulaiRawat !== '-') {
                        const umurDecimal = calculateUmur(item.id_lokasi, mulaiRawat, hasilPanenRawData);
                        calculatedUmur = umurDecimal !== null ? umurDecimal.toFixed(2) : '-';
                    }
                    
                    // Hitung semua nilai menggunakan fungsi perhitungan
                    const calculatedValues = calculateAllValues({
                        kcl: item.kcl || 0,
                        k2so4: item.k2so4 || 0,
                        za: item.za || 0,
                        dap: item.dap || 0,
                        urea: item.urea || 0
                    });
                    
                    // Default values
                    let displayLokasi = item.id_lokasi || '';
                    let displayLuasAktif = '-';
                    let displayStatus = '';
                    let displayWilayah = '-';
                    let displayJenisBibit = '-';
                    let displayKelasBibit = '-';
                    let displayTahun = '';
                    
                    if (lokasiInfo) {
                        // Jika ditemukan di tb_lokasi
                        displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                        displayLuasAktif = lokasiInfo.netto || '-';
                        displayStatus = lokasiInfo.status || '';
                        displayWilayah = lokasiInfo.wilayah || '-';
                        const rawBibit = lokasiInfo.bibit || '';
                        displayJenisBibit = getJenisBibit(rawBibit);
                        displayKelasBibit = getKelasBibit(rawBibit);
                        displayTahun = lokasiInfo.tahun || '';
                    }
                    
                    // Cari Plant Care dari data pj_wilayah berdasarkan nama lokasi
                    let plantCareDisplay = '-';
                    if (displayLokasi && displayLokasi !== '-') {
                        // Format lokasi untuk pencarian
                        const searchLokasi = formatText(displayLokasi).trim();
                        
                        // Cari di pjWilayahData berdasarkan lokasi
                        const pjItem = pjWilayahArray.find(pj => {
                            const pjLokasi = formatText(pj.lokasi).trim();
                            return pjLokasi.toLowerCase() === searchLokasi.toLowerCase();
                        });
                        
                        if (pjItem && pjItem.id_kepala_kebun_plant_care) {
                            // Cari nama plant care berdasarkan ID
                            const plantCareItem = plantCareArray.find(pc => 
                                pc.id === pjItem.id_kepala_kebun_plant_care
                            );
                            
                            if (plantCareItem) {
                                plantCareDisplay = plantCareItem.nama;
                            }
                            //else {
                            //    console.log("Plant Care tidak ditemukan untuk ID:", pjItem.id_kepala_kebun_plant_care);
                            //}
                        }
                        //else {
                        //    console.log("PJ Wilayah tidak ditemukan untuk lokasi:", searchLokasi);
                        //}
                    }
                    
                    return {
                        ...item,
                        lokasi: displayLokasi,
                        luas_aktif: displayLuasAktif,
                        status: displayStatus,
                        wilayah: displayWilayah,
                        plant_care: plantCareDisplay,
                        jenis_bibit: displayJenisBibit,
                        kelas_bibit: displayKelasBibit,
                        umur: calculatedUmur,
                        tahun: displayTahun,
                        mulai_rawat: mulaiRawat,
                        tanggal_closing: tanggalClosing || '-',
                        k: calculatedValues.k,
                        n: calculatedValues.n,
                        kNRatio: calculatedValues.knRatio,
                        setaraK2SO4: calculatedValues.setaraK2SO4,
                        setaraUrea: calculatedValues.setaraUrea
                    };
                });
                
                setOriginalDataList(updatedDataList);
                setDataList(updatedDataList);

                // Fetch lookup data for Status Lokasi (untuk warna indicator)
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

                // Set filter options berdasarkan data yang ada
                prepareFilterOptions(updatedDataList, null, pjWilayahArray, plantCareArray);

                // Debug: log data untuk verifikasi
                //console.log("Total data:", updatedDataList.length);
                //console.log("Contoh data dengan Plant Care:");
                //updatedDataList.slice(0, 5).forEach((item, index) => {
                //    console.log(`Item ${index}:`, {
                //        lokasi: item.lokasi,
                //        plant_care: item.plant_care,
                //        id_lokasi: item.id_lokasi
                //    });
                //});

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

// Fungsi helper untuk mendapatkan opsi filter dari data
const getFilterOptionsFromData = (dataList, fieldName) => {
    return [...new Set(dataList
        .map(item => item[fieldName])
        .filter(value => value && value !== '' && value !== '-'))] // Filter yang lebih ketat
        .map(value => ({ value, label: value }))
        .sort((a, b) => a.label.localeCompare(b.label));
};

// Fungsi untuk menyiapkan opsi filter berdasarkan data yang ada
const prepareFilterOptions = (dataList, statusConfig, pjWilayahArray, plantCareArray) => {
    // Status Lokasi - ambil langsung dari dataList, bukan dari config
    const statusOptions = [...new Set(dataList
        .map(item => item.status)
        .filter(value => value && value !== '' && value !== '-'))]
        .map(value => ({ value, label: value }))
        .sort((a, b) => a.label.localeCompare(b.label));
    
    setFilterOptions({
        statusLokasi: statusOptions,
        kodeWilayah: getFilterOptionsFromData(dataList, 'wilayah'),
        wilayahPlantCare: getFilterOptionsFromData(dataList, 'plant_care'),
        jenisBibit: getFilterOptionsFromData(dataList, 'jenis_bibit'),
        kelasBibit: getFilterOptionsFromData(dataList, 'kelas_bibit')
    });
};

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            const filtered = originalDataList.filter(item => {
                // Cek apakah cocok dengan nama lokasi (case-insensitive)
                const lokasiMatch = item.lokasi && 
                    item.lokasi.toLowerCase().includes(query);
                
                // Cek apakah cocok dengan ID lokasi (case-insensitive)
                const idMatch = item.id_lokasi && 
                    item.id_lokasi.toString().toLowerCase().includes(query);
                
                // Return true jika cocok dengan salah satu atau keduanya
                return lokasiMatch || idMatch;
            });
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Effect untuk menerapkan filter saat selectedFilters berubah
    useEffect(() => {
        applyFilters();
    }, [selectedFilters, originalDataList]);

// Fungsi untuk menerapkan filter berdasarkan selectedFilters
const applyFilters = () => {
    let filtered = [...originalDataList];
    
    // Filter berdasarkan Status Lokasi
    if (selectedFilters.statusLokasi) {
        filtered = filtered.filter(item => 
            item.status === selectedFilters.statusLokasi.value
        );
    }
    
    // Filter berdasarkan Kode Wilayah
    if (selectedFilters.kodeWilayah) {
        filtered = filtered.filter(item => 
            item.wilayah === selectedFilters.kodeWilayah.value
        );
    }
    
    // Filter berdasarkan Wilayah Plant Care
    if (selectedFilters.wilayahPlantCare) {
        filtered = filtered.filter(item => 
            item.plant_care === selectedFilters.wilayahPlantCare.value
        );
    }
    
    // Filter berdasarkan Jenis Bibit
    if (selectedFilters.jenisBibit) {
        filtered = filtered.filter(item => 
            item.jenis_bibit === selectedFilters.jenisBibit.value
        );
    }
    
    // Filter berdasarkan Kelas Bibit
    if (selectedFilters.kelasBibit) {
        filtered = filtered.filter(item => 
            item.kelas_bibit === selectedFilters.kelasBibit.value
        );
    }
    
    setDataList(filtered);
    setPage(0);
};

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            const updatedData = await refreshSetaraUreaData(database, onValuePromise);
            
            // Gunakan data lokasi yang sudah ada di state, jangan fetch ulang
            const lokasiArray = lokasiData;
            const mulaiRawatMap = lokasiMulaiRawatMap;
            
            // Update dengan data dari tb_lokasi jika tersedia
            const finalData = updatedData.map((item) => {
                const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);
                
                // Dapatkan tanggal mulai rawat dari map
                let mulaiRawat = mulaiRawatMap[item.id_lokasi] || '-';
                
                // Dapatkan tanggal closing dari hasil panen
                const tanggalClosing = getTanggalClosingFromHasilPanen(item.id_lokasi, hasilPanenData);
                
                // Hitung umur menggunakan fungsi calculateUmur
                let calculatedUmur = '-';
                if (mulaiRawat !== '-') {
                    const umurDecimal = calculateUmur(item.id_lokasi, mulaiRawat, hasilPanenData);
                    calculatedUmur = umurDecimal !== null ? umurDecimal.toFixed(2) : '-';
                }
                
                // Hitung semua nilai menggunakan fungsi perhitungan
                const calculatedValues = calculateAllValues({
                    kcl: item.kcl || 0,
                    k2so4: item.k2so4 || 0,
                    za: item.za || 0,
                    dap: item.dap || 0,
                    urea: item.urea || 0
                });
                
                // Default values
                let displayLokasi = item.id_lokasi || '';
                let displayLuasAktif = '-';
                let displayStatus = '';
                let displayWilayah = '-';
                let displayJenisBibit = '-';
                let displayKelasBibit = '-';
                let displayTahun = '';
                
                if (lokasiInfo) {
                    // Jika ditemukan di tb_lokasi
                    displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                    displayLuasAktif = lokasiInfo.netto || '-';
                    displayStatus = lokasiInfo.status || '';
                    displayWilayah = lokasiInfo.wilayah || '-';
                    const rawBibit = lokasiInfo.bibit || '';
                    displayJenisBibit = getJenisBibit(rawBibit);
                    displayKelasBibit = getKelasBibit(rawBibit);
                    displayTahun = lokasiInfo.tahun || '';
                }
                
                // Cari Plant Care dari data pj_wilayah berdasarkan nama lokasi
                let plantCareDisplay = '-';
                if (displayLokasi && displayLokasi !== '-') {
                    // Format lokasi untuk pencarian
                    const searchLokasi = formatText(displayLokasi).trim();
                    
                    // Cari di pjWilayahData berdasarkan lokasi
                    const pjItem = pjWilayahData.find(pj => {
                        const pjLokasi = formatText(pj.lokasi).trim();
                        return pjLokasi.toLowerCase() === searchLokasi.toLowerCase();
                    });
                    
                    if (pjItem && pjItem.id_kepala_kebun_plant_care) {
                        // Cari nama plant care berdasarkan ID
                        const plantCareItem = plantCareList.find(pc => 
                            pc.id === pjItem.id_kepala_kebun_plant_care
                        );
                        
                        if (plantCareItem) {
                            plantCareDisplay = plantCareItem.nama;
                        }
                    }
                }
                
                return {
                    ...item,
                    lokasi: displayLokasi,
                    luas_aktif: displayLuasAktif,
                    status: displayStatus,
                    wilayah: displayWilayah,
                    plant_care: plantCareDisplay,
                    jenis_bibit: displayJenisBibit,
                    kelas_bibit: displayKelasBibit,
                    umur: calculatedUmur,
                    tahun: displayTahun,
                    mulai_rawat: mulaiRawat,
                    tanggal_closing: tanggalClosing || '-',
                    k: calculatedValues.k,
                    n: calculatedValues.n,
                    kNRatio: calculatedValues.knRatio,
                    setaraK2SO4: calculatedValues.setaraK2SO4,
                    setaraUrea: calculatedValues.setaraUrea
                };
            });
            
            return finalData;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import khusus untuk setara urea per umur
            const result = await importExcelToFirebaseSetaraUrea(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui.`,
                    severity: "success",
                });
                
                // Set last updated time
                setLastUpdated(new Date());
                
                // Refresh data setelah import berhasil
                const finalData = await refreshDataAfterImport();
                
                setDataList(finalData);
                setOriginalDataList(finalData);
                
                // Update filter options dengan data baru
                prepareFilterOptions(finalData, null, pjWilayahData, plantCareList);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Handler untuk update filter (dipanggil dari FilterPanel)
    const handleUpdateFilter = (filterKey, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
    };

    // Handler untuk clear all filters
    const handleClearAllFilters = () => {
        setSelectedFilters({
            statusLokasi: null,
            kodeWilayah: null,
            wilayahPlantCare: null,
            jenisBibit: null,
            kelasBibit: null
        });
    };

    // Handler untuk refresh data (sama seperti di DashboardHeader)
    const handleRefresh = () => {
        setRefreshLoading(true);
        setTimeout(() => {
            setRefreshLoading(false);
            // Reset filter saat refresh
            handleClearAllFilters();
            
            // Dispatch custom event jika perlu
            window.dispatchEvent(new Event("dashboard-update"));
            
            setSnackbar({
                open: true,
                message: "Data telah direfresh.",
                severity: "success",
            });
        }, 1000);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/* Paper untuk AkumUreaBibitBesar */}
            {/*<Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 3 }}>*/}
                {/*<AkumUreaBibitBesar />*/}
                {/*<AkumUreaBibitSedang/>*/}
                {/*<AkumUreaBibitKecil/>*/}
                {/*<AkumUreaRatoon/>*/}
                {/*<GypsumPerMonth/>*/}
            {/*</Paper>*/}

            

            {/* Filter Panel baru dengan gaya DashboardHeader */}
            <FilterPanelMonitorSetaraUrea
                filterOpen={showFilterPanel}
                paperShadow={paperShadow}
                selectedFilters={selectedFilters}
                updateFilter={handleUpdateFilter}
                clearAllFilters={handleClearAllFilters}
                filterOptions={filterOptions}
            />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Setara Urea Per Umur
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
        placeholder="Cari berdasarkan lokasi..."
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
                            sx={{ width: 250 }}
                        />
    
                        {/* Icon untuk toggle filter - style sederhana */}
                        <Tooltip title={showFilterPanel ? "Sembunyikan Filter" : "Tampilkan Filter"}>
                            <IconButton 
                                color="primary" 
                                size="small" 
                                onClick={handleToggleFilterPanel}
                                sx={{
                                    backgroundColor: showFilterPanel ? 'action.selected' : 'transparent',
                                }}
                            >
                                {showFilterPanel ? (
                                    <FilterAltOutlinedIcon />
                                ) : (
                                    <FilterAltOffOutlinedIcon />
                                )}
                            </IconButton>
                        </Tooltip>

                        {/* Icon untuk refresh data - style sederhana */}
                        <Tooltip title="Refresh Data">
                            <span> {/* Wrapper untuk menangani disabled state */}
                                <IconButton 
                                    color="primary" 
                                    size="small" 
                                    onClick={handleRefresh}
                                    disabled={refreshLoading}
                                >
                                    {refreshLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>

                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="monitor setara urea per umur table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch">Lokasi</TableCell>
                                        <TableCell className="ded-tch">Status</TableCell>
                                        <TableCell className="ded-tch">Kode<br/>Wilayah</TableCell>
                                        <TableCell className="ded-tch">Wilayah<br/>Plant Care</TableCell>
                                        <TableCell className="ded-tch">Jenis<br/>Bibit</TableCell>
                                        <TableCell className="ded-tch">Kelas<br/>Bibit</TableCell>
                                        <TableCell className="ded-tch">Luas<br/>Aktif</TableCell>
                                        <TableCell className="ded-tch">Umur</TableCell>
                                        <TableCell className="ded-tch">Rencana Forcing</TableCell>
                                        <TableCell className="ded-tch">Real Forcing</TableCell>
                                        <TableCell className="ded-tch">GYPSUM</TableCell>
                                        <TableCell className="ded-tch">KCl</TableCell>
                                        <TableCell className="ded-tch">K2SO4</TableCell>
                                        <TableCell className="ded-tch">K</TableCell>
                                        <TableCell className="ded-tch">ZA</TableCell>
                                        <TableCell className="ded-tch">DAP</TableCell>
                                        <TableCell className="ded-tch">UREA</TableCell>
                                        <TableCell className="ded-tch">N</TableCell>
                                        <TableCell className="ded-tch">K/N<br/>Rasio</TableCell>
                                        <TableCell className="ded-tch">Setara<br/>K2SO4</TableCell>
                                        <TableCell className="ded-tch">Setara<br/>Urea</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            // Lookup nama_status_lokasi dan indicator dari config
                                            const statusLokasiItem = statusLokasiList.find(
                                                (item) => item.nama_status_lokasi === row.status
                                            );
                                            const displayStatusLokasi = row.status || '';
                                            const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                                            const mulaiRawat = lokasiMulaiRawatMap[row.id_lokasi] || '-';

                                            return (
                                                <TableRow key={row.firebaseKey}>

                                                    <TableCell className="ded-tbtrtc">
                                                        <Box>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                {formatText(row.lokasi)}
                                                            </div>
                                                            <div style={{ 
                                                                display: 'grid',
                                                                gridTemplateColumns: 'auto auto 1fr',
                                                                gridAutoRows: 'auto',
                                                                alignItems: 'center',
                                                                rowGap: '2px'
                                                            }}>
                                                                {/* Baris ID */}
                                                                <span style={{ 
                                                                    textAlign: 'left',
                                                                    fontSize: '0.8em',
                                                                    color: '#666',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    ID
                                                                </span>
                                                                <span style={{ 
                                                                    fontSize: '0.8em',
                                                                    color: '#666',
                                                                    fontWeight: '500',
                                                                    padding: '0 4px'
                                                                }}>
                                                                    :
                                                                </span>
                                                                <span style={{ 
                                                                    fontSize: '0.8em',
                                                                    color: '#666'
                                                                }}>
                                                                    {row.id_lokasi || '-'}
                                                                </span>
                                                                
                                                                {/* Baris Rawat */}
                                                                <span style={{ 
                                                                    textAlign: 'left',
                                                                    fontSize: '0.8em',
                                                                    color: '#666',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    Rawat
                                                                </span>
                                                                <span style={{ 
                                                                    fontSize: '0.8em',
                                                                    color: '#666',
                                                                    fontWeight: '500',
                                                                    padding: '0 4px'
                                                                }}>
                                                                    :
                                                                </span>
                                                                <span style={{ 
                                                                    fontSize: '0.8em',
                                                                    color: '#666'
                                                                }}>
                                                                    {mulaiRawat}
                                                                </span>
                                                                
                                                                {/* Baris Closing (jika ada) */}
                                                                {row.tanggal_closing && row.tanggal_closing !== '-' && (
                                                                    <>
                                                                        <span style={{ 
                                                                            textAlign: 'left',
                                                                            fontSize: '0.8em',
                                                                            color: '#666',
                                                                            fontWeight: '500'
                                                                        }}>
                                                                            Closing
                                                                        </span>
                                                                        <span style={{ 
                                                                            fontSize: '0.8em',
                                                                            color: '#666',
                                                                            fontWeight: '500',
                                                                            padding: '0 4px'
                                                                        }}>
                                                                            :
                                                                        </span>
                                                                        <span style={{ 
                                                                            fontSize: '0.8em',
                                                                            color: '#666'
                                                                        }}>
                                                                            {row.tanggal_closing}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell>
                                                        {displayStatusLokasi ? (
                                                            <Chip
                                                                label={displayStatusLokasi}
                                                                style={{ 
                                                                    backgroundColor: statusIndicatorColor, 
                                                                    color: theme.palette.getContrastText(statusIndicatorColor) 
                                                                }}
                                                                size="small"
                                                            />
                                                        ) : '-'}
                                                    </TableCell>
                                                    
                                                    {/* Sel Kode Wilayah */}
                                                    <TableCell>{formatText(row.wilayah)}</TableCell>
                                                    
                                                    {/* Sel Wilayah Plant Care */}
                                                    <TableCell>{formatText(row.plant_care)}</TableCell>
                                                    
                                                    <TableCell>{formatText(row.jenis_bibit)}</TableCell>
                                                    <TableCell>{formatText(row.kelas_bibit)}</TableCell>
                                                    <TableCell>{row.luas_aktif}</TableCell>
                                                    <TableCell>{row.umur}</TableCell>
                                                    <TableCell>{displayFormatDateToDDMMYYYY(row.rencana_forcing)}</TableCell>
                                                    <TableCell>{displayFormatDateToDDMMYYYY(row.real_forcing)}</TableCell>
                                                    <TableCell>{formatDecimal(row.gypsum)}</TableCell>
                                                    <TableCell>{formatDecimal(row.kcl)}</TableCell>
                                                    <TableCell>{formatDecimal(row.k2so4)}</TableCell>
                                                    <TableCell>{formatDecimal(row.k, 2)}</TableCell>
                                                    <TableCell>{formatDecimal(row.za)}</TableCell>
                                                    <TableCell>{formatDecimal(row.dap)}</TableCell>
                                                    <TableCell>{formatDecimal(row.urea)}</TableCell>
                                                    <TableCell>{formatDecimal(row.n, 2)}</TableCell>
                                                    <TableCell>{formatDecimalWithTwoDigits(row.kNRatio, 2)}</TableCell>
                                                    <TableCell>{formatDecimal(row.setaraK2SO4, 2)}</TableCell>
                                                    <TableCell>{formatDecimal(row.setaraUrea, 2)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery || Object.values(selectedFilters).some(f => f !== null) 
                                ? 'Tidak ada data yang sesuai dengan pencarian/filter' 
                                : 'Tidak ada data monitor setara urea per umur.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MonitorSetaraUreaPerUmur;